import { supabase } from '../../../lib/supabase';
import type { MachineSpecificationValue, MachineTemplate, SpecificationKey } from '../types';

// Shared by both the planning read-hook (useMachineTemplates) and the settings CRUD-hook
// (useManageMachineTemplates) so the query/shape can't drift between the two.
export const MACHINE_TEMPLATE_SELECT =
  'id, name, organization_id, category_id, engine_type, supplier_id, archived, created_at, category:categories(id, name), supplier:suppliers(id, name)';

interface TemplateOptionRow {
  template_id: string;
  option: { id: string; name: string } | null;
}

interface TemplateSpecificationRow {
  template_id: string;
  specification_key: SpecificationKey;
  value: number;
}

// Options and suggested specification prefill values are each joined via a separate flat
// query (not a nested one-to-many select) — see CLAUDE.md's "split queries" rule for
// bookings: a LATERAL JOIN for a one-to-many relation is avoided on purpose.
export async function attachMachineTemplateOptionsAndSpecifications(
  templates: MachineTemplate[],
): Promise<MachineTemplate[]> {
  const ids = templates.map((t) => t.id);
  if (!ids.length) return templates;

  const [{ data: optionRows }, { data: specRows }] = await Promise.all([
    supabase.from('machine_template_options').select('template_id, option:options(id, name)').in('template_id', ids),
    supabase.from('machine_template_specifications').select('template_id, specification_key, value').in('template_id', ids),
  ]);

  const optionsByTemplate = new Map<string, { id: string; name: string }[]>();
  for (const row of (optionRows ?? []) as unknown as TemplateOptionRow[]) {
    if (!row.option) continue;
    const list = optionsByTemplate.get(row.template_id) ?? [];
    list.push(row.option);
    optionsByTemplate.set(row.template_id, list);
  }

  const specsByTemplate = new Map<string, MachineSpecificationValue[]>();
  for (const row of (specRows ?? []) as TemplateSpecificationRow[]) {
    const list = specsByTemplate.get(row.template_id) ?? [];
    list.push({ key: row.specification_key, value: row.value });
    specsByTemplate.set(row.template_id, list);
  }

  return templates.map((t) => {
    const options = optionsByTemplate.get(t.id) ?? [];
    return {
      ...t,
      options,
      option_ids: options.map((o) => o.id),
      specifications: specsByTemplate.get(t.id) ?? [],
    };
  });
}
